import { KINDS, SKILL_IDS, TOTEM_KINDS } from '@orb/sim';

/**
 * Bumped on any incompatible change. Checked once, in `hello`: a mismatch gets
 * `error(version_mismatch)` and a close with the same code, so messages don't repeat it.
 * 5: the gate falls on its tenth breach (sim STATE_VERSION 4) — older clients would replay differently.
 */
export const PROTOCOL_VERSION = 11;

/** Message codes. The first byte of every message; client → server 1–31, server → client 32–63. */
export const C2S = {
  hello: 1, create_room: 2, join_room: 3, queue: 4, leave: 5, input: 6, checksum: 7, ping: 8,
  /** Reply to `snapshot_request`, possibly in several parts. */
  snapshot: 9,
  /** After a resumed `room`: "I have every turn before fromTick" — the server sends the rest. */
  resync: 10,
  /** A result claim: "at this tick my state hashes to …, my tally is …". The server replays and judges. */
  result: 11,
} as const;
export const S2C = {
  welcome: 32, room: 33, turn: 34, player_joined: 35, player_left: 36,
  snapshot_request: 37, snapshot: 38, desync: 39, pong: 40, error: 41,
  /** The server's judgement of a result claim, with the tally it found itself. */
  verdict: 42,
  /** A random match was found, its room is on another server instance: reconnect there and join `code`. */
  match: 43,
} as const;

/**
 * Errors, also used as WebSocket close codes (4000–4999 are free for applications).
 * An `error` message may precede a close with the same code, or stand alone if the socket stays open.
 */
export const ERROR = {
  bad_message: 4000,
  version_mismatch: 4001,
  unauthorized: 4003,
  room_not_found: 4004,
  room_full: 4005,
  not_in_room: 4006,
  already_in_room: 4007,
  /** Already waiting in the matchmaking queue. */
  already_queued: 4012,
  /** The match cannot be rebuilt for this client (no turn history and no peer to ask). */
  resync_unavailable: 4013,
  /** This session was resumed on another connection; this one is closed. */
  replaced: 4014,
  queue_timeout: 4008,
  bad_input_tick: 4009,
  rate_limited: 4029,
  /** The connection could not keep up with the turns and was cut off. */
  too_slow: 4030,
  /** Nothing from the client for too long (a frozen or cached page whose socket stayed open). */
  silent: 4031,
  internal: 4500,
  /** The server does not offer this yet (e.g. matchmaking before stage 6). */
  not_implemented: 4501,
  server_full: 4503,
} as const;
export type ErrorName = keyof typeof ERROR;
export type ErrorCode = (typeof ERROR)[ErrorName];
const ERROR_BY_CODE = new Map<number, ErrorName>(Object.entries(ERROR).map(([k, v]) => [v, k as ErrorName]));
export const errorName = (code: number): ErrorName | null => ERROR_BY_CODE.get(code) ?? null;

/** Game modes on the wire (indices). PvP will append, never reorder. */
export const MODES = ['solo', 'coop'] as const;
export type Mode = (typeof MODES)[number];

/** Action types and their enumerations on the wire (indices into the sim's own tables). */
export const ACTIONS = ['summon', 'merge', 'cast', 'equip_totem', 'equip_skill', 'set_deck', 'shatter'] as const;
export { KINDS, SKILL_IDS, TOTEM_KINDS };

/**
 * A full room waits this long before its first turn: both players see «found» and a countdown while
 * the battle loads. Not on the wire — clients count it from the moment they see the room full.
 */
export const MATCH_COUNTDOWN_MS = 3000;

/** Why a player left a room. */
export const LEAVE_REASONS = ['left', 'disconnected', 'timeout', 'kicked'] as const;
export type LeaveReason = (typeof LEAVE_REASONS)[number];

/** Room codes: 6 characters from an alphabet without the look-alikes 0/O and 1/I. */
export const ROOM_CODE_LENGTH = 6;
export const ROOM_CODE_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
/** Trims, upper-cases and validates a typed code; null if it cannot be a room code. */
export function normalizeRoomCode(input: string): string | null {
  const code = input.trim().toUpperCase();
  if (code.length !== ROOM_CODE_LENGTH) return null;
  for (const ch of code) if (!ROOM_CODE_ALPHABET.includes(ch)) return null;
  return code;
}
/** A room code from 6 random bytes (the server supplies cryptographic randomness). */
export function roomCodeFrom(random: Uint8Array, instance?: number): string {
  if (random.length < ROOM_CODE_LENGTH) throw new Error('need 6 random bytes');
  let code = '';
  // 32 symbols: the low 5 bits of each byte are uniform
  for (let i = 0; i < ROOM_CODE_LENGTH; i++) code += ROOM_CODE_ALPHABET[random[i] & 31];
  return instance === undefined ? code : instanceKey(instance) + code.slice(1);
}

/**
 * Several server processes share the load; a room lives on one of them. The first character of its
 * code names that instance, and clients pass it as `?i=` so the reverse proxy sends them there.
 */
export const MAX_INSTANCES = ROOM_CODE_ALPHABET.length;
export const instanceKey = (instance: number) => ROOM_CODE_ALPHABET[instance];
export const instanceOfRoom = (code: string) => ROOM_CODE_ALPHABET.indexOf(code[0]);
/** Sets (or, with null, removes) query parameters of a URL; plain string work, no DOM needed. */
export function withQuery(url: string, set: Record<string, string | null>): string {
  const [base, query = ''] = url.split('?', 2);
  const kept = query.split('&').filter((p) => p && !(p.split('=')[0] in set));
  for (const [k, v] of Object.entries(set)) if (v !== null) kept.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  return kept.length ? `${base}?${kept.join('&')}` : base;
}
/** The socket URL that reaches the instance holding `code` (any other query is kept). */
export const routedUrl = (url: string, code: string) => withQuery(url, { q: null, i: code[0] });

/** Lockstep timing shared by the server and clients. */
export const LOCKSTEP = {
  /** Simulation ticks per second. */
  tickRate: 60,
  /** Turns per second: each turn covers `ticksPerTurn` ticks. */
  turnRate: 20,
  ticksPerTurn: 3,
  /** Input delay in ticks: default and adaptive bounds. */
  inputDelay: 6, minInputDelay: 4, maxInputDelay: 12,
  /** Clients report a checksum every this many ticks. */
  checksumEvery: 60,
} as const;

/** Session resume window after a disconnect, ms (also the server's empty-room lifetime). */
export const RESUME_TTL_MS = 60000;
/** Most ticks one catch-up `turn` message may cover (its `ticks` field is a byte). */
export const MAX_TURN_TICKS = 255;

/** Biggest snapshot part payload: keeps every message under the server's 4 KB frame limit. */
export const SNAPSHOT_CHUNK = 3584;
